/**
 * Decide how many items from the front of a list can be rendered visibly
 * given the available width.
 *
 * If everything (items + gaps) fits without the overflow trigger, returns
 * `widths.length`. Otherwise, accounts for the overflow trigger button and
 * returns the largest prefix length that still fits.
 *
 * @param widths     measured width of each item in render order
 * @param overflowTriggerWidth measured width of the "..." trigger
 * @param available  available width — pass `Math.floor(entry.contentRect.width)` or
 *                   `entry.contentRect.width - 1` to guard against sub-pixel rounding
 *                   that can otherwise flicker the overflow trigger at borderline widths
 * @param gap        gap between items (must match CSS gap)
 */
export function computeMaxVisible(
  widths: readonly number[],
  overflowTriggerWidth: number,
  available: number,
  gap: number
): number {
  if (widths.length === 0) return 0;
  // Note: gap accounting is correct (N items → N-1 gaps); the original
  // NavBar.vue inline impl charged N gaps. Kept intentionally.

  const totalWithoutTrigger = widths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);
  if (totalWithoutTrigger <= available) return widths.length;

  let remaining = available - overflowTriggerWidth - gap;
  let count = 0;
  for (const w of widths) {
    const cost = count === 0 ? w : w + gap;
    if (remaining < cost) break;
    remaining -= cost;
    count += 1;
  }
  return count;
}
